import React, {ReactElement, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Renderer} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {computeCustomFieldIconName} from '../../../../helpers/customFieldViewHelpers';
import {useClipboard} from '../../../../helpers/hooks/clipboardHookHelpers';
import {buildCustomFieldPublicId} from '../../../../helpers/models/customFieldHelpers';
import {CustomFieldModel, CustomFieldResourceTypesEnum} from '../../../../models/api/customFieldModel';
import {Skeleton} from '../../skeleton';
import {ExpandableResourceCell, ExpandableResourceCellIconProps} from './expandableResourceCell';
import {TableCellPropsWithAvatar} from './tableCell';

/*
 * Constants.
 */

const maxCheckboxTooltipWidth = 190; // px.

/*
 * Props.
 */

interface CustomFieldCellProps extends TableCellPropsWithAvatar<CustomFieldModel> {
  isDefaultField?: boolean;
  iconName?: IconName;
  renderDefaultPillTooltip?: Renderer;
}

/*
 * Styles.
 */

const StyledSkeleton = styled(Skeleton)`
  height: 16px;
  align-self: center;
  margin-left: 12px;
`;

const StyledSimpleDropdown = styled(SimpleDropdown)`
  z-index: 1;
`;

const StyledCustomFieldDefaultPillDiv = styled.div`
  display: inline-block;
  padding: 2px 4px;
  border-radius: 7px;
  text-transform: uppercase;
  background: ${(p) => p.theme.greys.shade30};
  font-weight: ${fontWeights.bold};
  font-size: 8px;
  line-height: 10px;
  margin-left: 4px;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  display: inline-block;
  pointer-events: all;
  margin-top: -2px;
`;

/*
 * Component.
 */

export const CustomFieldCell = (props: CustomFieldCellProps): ReactElement => {
  const theme = useUserTheme();
  const {copy} = useClipboard();
  const {
    isDefaultField,
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    iconName,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    renderDefaultPillTooltip,
    value,
  } = props;

  const [customFieldIdWithDropdownOpened, setCustomFieldIdWithDropdownOpened] = useState(-1);

  const renderTooltip = () => {
    if (!isDefaultField) {
      return null;
    }
    return (
      <StyledTooltipCoordinator
        render={renderDefaultPillTooltip}
        placement="top"
        maxWidth={maxCheckboxTooltipWidth}
      >
        <StyledCustomFieldDefaultPillDiv>
          <Trans id="custom-field-tab-default-pill">default</Trans>
        </StyledCustomFieldDefaultPillDiv>
      </StyledTooltipCoordinator>
    );
  };

  const onCopyPublicIdClick = (customField?: CustomFieldModel) => {
    if (!customField) {
      return;
    }

    copy(buildCustomFieldPublicId(customField));
  };

  const renderDropdown = (popoverProps: PopoverTransferProps) => (
    <StyledSimpleDropdown {...popoverProps}>
      <SimpleDropdownItem onSelect={() => onCopyPublicIdClick(value)}>
        <Trans id="custom-field-list-menu-copy-id">Copy custom field ID</Trans>
      </SimpleDropdownItem>
    </StyledSimpleDropdown>
  );

  if (!value) {
    return <StyledSkeleton scheme={settings?.loading?.style} />;
  }

  const iconProps: ExpandableResourceCellIconProps = {
    name: iconName ?? computeCustomFieldIconName(value.type),
    color: theme.greys.shade70,
    borderColor: theme.greys.shade30,
    backgroundColor: theme.greys.shade30,
  };

  return (
    <ExpandableResourceCell<CustomFieldModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      nestingLevel={0}
      hasChildren={false}
      shouldShowSubtitle={Boolean(value.description)}
      shouldHideActions={Boolean(value.resourceType !== CustomFieldResourceTypesEnum.TEAMMATE)}
      onActionsDropdownOpened={(isOpen) => setCustomFieldIdWithDropdownOpened(isOpen ? value.id : -1)}
      renderActionsDropdown={({modalProps}) => renderDropdown(modalProps)}
      hasChevron={false}
      hasHover={value.id === customFieldIdWithDropdownOpened}
      isExpanded={false}
      renderSubtitle={() => value.description}
      iconProps={iconProps}
      renderTooltip={renderTooltip}
    />
  );
};
